import { useEffect, useMemo, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { PageLoader } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import type { MensajeChat } from '@/types/conversations';
import { MessageBubble } from './MessageBubble';
import { describeCount, groupMessagesByDay } from './inbox-format';

/**
 * Historial de la conversación abierta (el chat).
 *
 * El historial se pagina por CURSOR y hacia atrás (`useConversationMessages`): al abrir se ven los
 * últimos `MENSAJES_POR_PAGINA` y "Cargar mensajes anteriores" trae la página previa. Por eso el
 * botón está ARRIBA del hilo y no abajo: se carga hacia el pasado, que es lo que queda por encima
 * de lo que ya se ve.
 *
 * El scroll es del contenedor (no de la página) para que la bandeja y la cabecera del contacto
 * sigan visibles mientras se lee.
 *
 * **El hilo se coloca en lo último** (`useEffect` sobre el último mensaje): un chat se lee de abajo
 * arriba y al abrir la conversación lo que interesa es lo que se acaba de decir, no el mensaje más
 * viejo de la página. Sin esto, en una ventana baja (donde solo caben dos o tres burbujas) el
 * navegador dejaba el scroll arriba y la conversación parecía vacía: solo se veía el separador del
 * día. Cargar mensajes ANTERIORES no cambia el último mensaje, así que la posición de quien está
 * leyendo hacia atrás no salta. Y como las FOTOS no tienen alto hasta que el navegador las carga (un
 * chat que termina en foto crecería después de bajar el scroll y la burbuja quedaría fuera), el
 * contenido se observa con un `ResizeObserver` que solo reengancha si ya se estaba abajo.
 */

/**
 * Margen (en píxeles) con el que se considera que se está leyendo lo último. Solo se usa para no
 * arrastrar al final a quien subió a mirar el historial.
 */
const MARGEN_AL_FINAL = 24;

/** ¿El contenedor del hilo está abajo del todo (dentro del margen)? */
function pegadoAlFinal(contenedor: HTMLElement): boolean {
  return contenedor.scrollHeight - contenedor.scrollTop - contenedor.clientHeight <= MARGEN_AL_FINAL;
}

export interface ConversationThreadProps {
  mensajes: MensajeChat[];
  /** Total de mensajes de la conversación (para decir cuánto historial falta). */
  total: number;
  /** Nombre del contacto, para el `aria-label` de la región. */
  contacto: string;
  isPending: boolean;
  error: unknown;
  /** ¿Quedan mensajes más antiguos? (`hayMas`/`siguienteCursor` del backend). */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  /** Reenvía un saliente que el canal rechazó (ver `MessageBubble`); sin él no se pinta el botón. */
  onResend?: (mensaje: MensajeChat) => void;
  /** Id del mensaje que se está reenviando (para dejar su botón cargando). */
  resendingId?: string | null;
}

export function ConversationThread({
  mensajes,
  total,
  contacto,
  isPending,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  onRefresh,
  refreshing,
  onResend,
  resendingId = null,
}: ConversationThreadProps) {
  // Se agrupa por día (bloques "Hoy"/"Ayer"/fecha) porque un chat de varias semanas sin separadores
  // es una lista de burbujas sin referencia temporal.
  const bloques = useMemo(() => groupMessagesByDay(mensajes), [mensajes]);

  const contenedorRef = useRef<HTMLDivElement>(null);
  const contenidoRef = useRef<HTMLDivElement>(null);
  const ultimoId = mensajes.length > 0 ? mensajes[mensajes.length - 1].id : null;

  useEffect(() => {
    const contenedor = contenedorRef.current;
    const contenido = contenidoRef.current;
    if (!contenedor || !contenido) return;

    const irAlFinal = (): void => {
      contenedor.scrollTop = contenedor.scrollHeight;
    };

    irAlFinal();

    // Las fotos no tienen alto hasta que el navegador las carga, así que el hilo crece un instante
    // DESPUÉS de este efecto: cuando la conversación termina en foto, la burbuja se quedaba entera
    // por debajo de la ventana (el scroll se calculó con la imagen a cero). Mientras el contenido
    // siga creciendo se vuelve a bajar —y **solo si se estaba abajo del todo**, así que a quien está
    // leyendo hacia arriba no se le mueve nada.
    const observador = new ResizeObserver(() => {
      if (pegadoAlFinal(contenedor)) irAlFinal();
    });

    observador.observe(contenido);

    return () => observador.disconnect();
  }, [ultimoId]);

  return (
    <section
      aria-label={`Historial de ${contacto}`}
      className="flex min-h-0 flex-1 flex-col border-t border-line"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-2">
        <p className="text-caption text-ink-muted">
          {describeCount(mensajes.length, 'mensaje cargado', 'mensajes cargados')} de {total}
          {hasMore ? ' · el historial sigue más arriba' : ''}
        </p>

        <span className="flex items-center gap-2">
          {hasMore ? (
            <Button variant="secondary" size="sm" loading={loadingMore} onClick={onLoadMore}>
              Cargar mensajes anteriores
            </Button>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            aria-label="Refrescar el historial"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <RefreshCw size={16} className={cn(refreshing && 'animate-spin')} />
          </Button>
        </span>
      </div>

      <div ref={contenedorRef} className="scroll-thin min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        {/* Envoltorio propio (no cambia el layout: es un bloque) porque es lo que se observa para
            saber si el hilo creció — por ejemplo cuando termina de cargar una foto. */}
        <div ref={contenidoRef}>
          {isPending ? <PageLoader label="Cargando el historial…" /> : null}

          {error ? <Alert>{getErrorMessage(error)}</Alert> : null}

          {!isPending && !error && mensajes.length === 0 ? (
            <p className="py-8 text-center text-caption text-ink-muted">
              Esta conversación todavía no tiene mensajes: el bot la abrió al recibir el primer
              mensaje del contacto y no hay nada guardado aún.
            </p>
          ) : null}

          {bloques.map((bloque) => (
            <div key={bloque.key}>
              <div className="my-3 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" aria-hidden="true" />
                <span className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                  {bloque.label}
                </span>
                <span className="h-px flex-1 bg-line" aria-hidden="true" />
              </div>

              <ul className="space-y-2">
                {bloque.mensajes.map((mensaje) => (
                  <MessageBubble
                    key={mensaje.id}
                    mensaje={mensaje}
                    onResend={onResend}
                    resending={resendingId === mensaje.id}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
